import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronLeft } from 'lucide-react';
import FontStyles from '../components/shared/FontStyles.jsx';
import FoxMark from '../components/shared/FoxMark.jsx';
import RootedWordmark from '../components/shared/RootedWordmark.jsx';
import {
  PAPER,
  PAPER_2,
  INK,
  INK_MID,
  INK_SOFT,
  LINE,
  ACCENT,
} from '../components/shared/theme.js';
import { useGuide } from '../hooks/useGuide.js';
import { getRandomName } from '../data/guideNames.js';

function StepHeader({ step, total, onBack }) {
  const pct = (step / total) * 100;
  return (
    <>
      <div className="flex items-center justify-between px-6 pt-5 pb-3">
        <button
          onClick={onBack}
          className="p-2 -ml-2 rounded-full transition active:scale-95"
          style={{ visibility: step === 1 ? 'hidden' : 'visible' }}
        >
          <ChevronLeft size={20} strokeWidth={1.5} style={{ color: INK }} />
        </button>
        <RootedWordmark size="sm" />
        <FoxMark size={18} />
      </div>
      <div
        className="px-6 pb-2 smallcaps ff-ui"
        style={{ color: INK_SOFT, textAlign: 'center' }}
      >
        Step {step} of {total}
      </div>
      <div className="px-7 pb-1">
        <div
          className="h-[2px] w-full rounded-full overflow-hidden"
          style={{ background: 'rgba(27,24,20,0.08)' }}
        >
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${pct}%`, background: ACCENT }}
          />
        </div>
      </div>
    </>
  );
}

// =====================================================================
// STEP 1 — Gender selection
// =====================================================================
function GenderStep({ onSelect, onBack }) {
  return (
    <div className="screen-in relative grain min-h-screen flex flex-col w-full max-w-[440px] mx-auto">
      <StepHeader step={1} total={2} onBack={onBack} />

      <div className="px-7 pt-7 pb-2">
        <div className="smallcaps ff-ui" style={{ color: ACCENT }}>
          Begin
        </div>
        <h2
          className="ff-display mt-3 leading-[1.05]"
          style={{ color: INK, fontSize: 30, fontWeight: 400 }}
        >
          First, choose the voice of your guide
        </h2>
        <p
          className="ff-display italic mt-3 leading-[1.5]"
          style={{ color: INK_MID, fontSize: 15 }}
        >
          Your guide will walk with you through everything ahead.
        </p>
      </div>

      <div className="flex-1 px-6 mt-6 grid grid-cols-2 gap-3">
        {[
          { id: 'female', label: 'Female Voice' },
          { id: 'male', label: 'Male Voice' },
        ].map((opt) => (
          <button
            key={opt.id}
            onClick={() => onSelect(opt.id)}
            className="rounded-2xl p-5 flex flex-col items-center gap-4 transition-all active:scale-[0.98]"
            style={{
              background: PAPER_2,
              border: `1px solid ${LINE}`,
              minHeight: 220,
            }}
          >
            <div className="flex-1 flex items-center justify-center w-full">
              <FoxMark size={64} variant="gold" />
            </div>
            <div className="ff-display" style={{ color: INK, fontSize: 18, fontWeight: 500 }}>
              {opt.label}
            </div>
            <div
              className="ff-ui text-[11px]"
              style={{ color: INK_SOFT, letterSpacing: '0.05em' }}
            >
              Tap to choose
            </div>
          </button>
        ))}
      </div>

      <div className="px-7 pb-10 pt-6 text-center">
        <p
          className="ff-display italic"
          style={{ color: INK_SOFT, fontSize: 12 }}
        >
          Your guide is your companion — not a chatbot.
        </p>
      </div>
    </div>
  );
}

// =====================================================================
// STEP 2 — Guide introduction (Begin → straight to intake)
// =====================================================================
function GuideIntro({ guideName, onBegin, onBack }) {
  return (
    <div className="screen-in relative grain min-h-screen flex flex-col w-full max-w-[440px] mx-auto">
      <StepHeader step={2} total={2} onBack={onBack} />

      <div className="flex-1 flex flex-col items-center justify-center px-9 text-center">
        <div className="fox-glow">
          <FoxMark size={86} />
        </div>

        <span className="block hairline-gold h-px w-32 mt-7" />

        <div
          className="mt-5 ff-mono"
          style={{ color: INK_SOFT, fontSize: 10, letterSpacing: '0.22em' }}
        >
          MEET YOUR GUIDE
        </div>

        <h2
          className="ff-display mt-3 leading-none"
          style={{
            color: INK,
            fontSize: 56,
            fontWeight: 300,
            letterSpacing: '0.02em',
          }}
        >
          {guideName}
        </h2>

        <p
          className="ff-display italic mt-7 leading-[1.5] max-w-[320px]"
          style={{ color: INK_MID, fontSize: 17 }}
        >
          "Hi, I'm {guideName}. I'll be with you every step of the way."
        </p>

        <div className="w-full max-w-[320px] mt-10">
          <button
            onClick={onBegin}
            className="w-full py-4 rounded-full ff-ui font-medium text-[14px] flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
            style={{ background: INK, color: PAPER }}
          >
            Begin
            <ArrowRight size={16} strokeWidth={1.75} />
          </button>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// MAIN — voice → meet your guide → intake
// =====================================================================
export default function GuideSetup() {
  const navigate = useNavigate();
  const { guide, updateGuide } = useGuide();

  // sub-step within this screen: 'gender' | 'intro'
  const [step, setStep] = useState('gender');

  const handleGender = (gender) => {
    console.log('[Guide] gender set to:', gender);
    const name = getRandomName(gender);
    // Tone selection was removed — always default to 'warm' so the
    // downstream prompt builders that expect a tone keep working.
    updateGuide({ gender, name, tone: 'warm' });
    setStep('intro');
  };

  const goBack = () => {
    if (step === 'intro') setStep('gender');
    else navigate('/');
  };

  const beginIntake = () => navigate('/intake');

  return (
    <div className="min-h-screen w-full ff-ui" style={{ background: PAPER, color: INK }}>
      <FontStyles />
      {step === 'gender' && (
        <GenderStep onSelect={handleGender} onBack={goBack} />
      )}
      {step === 'intro' && (
        <GuideIntro
          guideName={guide.name}
          onBegin={beginIntake}
          onBack={goBack}
        />
      )}
    </div>
  );
}
